import {Injectable} from '@angular/core';
import {HttpResponse} from '@angular/common/http';
import {Optional} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/filter';
import 'rxjs/add/observable/throw';
import {Report} from '../models/report.model';
import {
  DealPayrollBreakdown,
  DEAL_PAYROLL_FORMULA_VERSION,
  hasExactDealPayrollMemoryMatch,
  isDealPayrollIdentityMatch,
  isDealPayrollPeriodDiagnostic,
  isPlanfixDealPayroll
} from '../models/deal-payroll.model';
import {DealPayrollService} from './deal-payroll.service';
import {DealPayrollGroupReportRequest} from './deal-payroll.service.types';
import {UserService} from './user.service';
import {UserReportService} from './user-report.service';
import {USER_REPORT_SERVICE_REQUIRED} from './user-report.constants';
import {
  DATE_PART_SEPARATOR,
  DEAL_PAYROLL_SERVICE_REQUIRED,
  FIRST_REPORT_INDEX,
  LAST_DAY_OF_PREVIOUS_MONTH,
  MINIMUM_TASK_LINE_COUNT,
  MONTH_CAPTURE_INDEX,
  MONTH_RANGE_PATTERN,
  NUMBER_VALUE_TYPE,
  PAYROLL_KEY_SEPARATOR,
  REPORT_DATE_PATTERN,
  REPORT_FROM_CAPTURE_INDEX,
  REPORT_SERVICE_EMPTY_VALUE,
  REPORT_TO_CAPTURE_INDEX,
  YEAR_CAPTURE_INDEX
} from './report.service.constants';
import {FreshReportContext, GroupPayrollReportKey, GroupPayrollReportRequest,
  GroupReportPreparation, HourlyComparisonRow, PreparedGroupReportResponse,
  PayrollRetentionContext, ReportSelectionContext} from './report.service.types';

export {GroupPayrollReportKey} from './report.service.types';
const FINANCIAL_REPORT_STORAGE_KEY = 'Reports';
const INITIAL_REPORT_REVISION = 0;
const NEXT_REPORT_REVISION = 1;
const COMPARISON_CONTEXT_UNAVAILABLE = 'Hourly comparison context is unavailable';

export function dealPayrollMonthFromRange(from: string, to: string): string | null {
  if (!from || !to) {
    return null;
  }
  const match = MONTH_RANGE_PATTERN.exec(from);
  if (!match) {
    return null;
  }
  const year = Number(match[YEAR_CAPTURE_INDEX]);
  const month = Number(match[MONTH_CAPTURE_INDEX]);
  const lastDay = new Date(year, month, LAST_DAY_OF_PREVIOUS_MONTH).getDate();
  const expectedMonth = match[YEAR_CAPTURE_INDEX] + DATE_PART_SEPARATOR + match[MONTH_CAPTURE_INDEX];
  return to === expectedMonth + DATE_PART_SEPARATOR + String(lastDay) ? expectedMonth : null;
}

@Injectable()
export class ReportService {
  reportsMin = [];
  filteredReports: Report[] = [];
  allReports: Report[];

  rowNames: any[] = [];

  reportDate = '';

  public data = [];
  private dealPayrollBreakdowns: {[key: string]: DealPayrollBreakdown} = {};
  private groupPayrollReportKeyValue = REPORT_SERVICE_EMPTY_VALUE;
  private groupPayrollOwner = REPORT_SERVICE_EMPTY_VALUE;
  private groupPayrollReports: Report[] = [];
  private sessionOwner = REPORT_SERVICE_EMPTY_VALUE;
  private freshReportSignature: string = null;
  private groupPreparation: GroupReportPreparation = null;
  private groupPreparationRequest: FreshReportContext = null;
  private currentDealPayrollMonth: string = null;
  private readonly payrollInvalidation = new Subject<void>();
  private readonly reportStorage: Storage = localStorage;
  readonly payrollReportsInvalidated$ = this.payrollInvalidation.asObservable();
  payrollReportRevision = INITIAL_REPORT_REVISION;

  constructor(
    @Optional() private dealPayrollService?: DealPayrollService,
    @Optional() private userService?: UserService,
    @Optional() private userReportService?: UserReportService
  ) {
  }

  getDealPayrollGroupReport(request: DealPayrollGroupReportRequest): Observable<Report[]> {
    return this.requiredDealPayrollService().getGroupPayrollReport(request);
  }

  getReportByGroupId(request: GroupPayrollReportRequest): Observable<HttpResponse<Report[]>> {
    const context = this.captureFreshReportContext(request.from, request.to, request.groupId);
    this.groupPreparationRequest = context;
    this.groupPreparation = null;
    return this.userService.getReportByGroupId(request).do((response: PreparedGroupReportResponse) => {
      this.ensureSessionOwner();
      if (this.groupPreparationRequest !== context || context.owner !== this.sessionOwner
        || context.revision !== this.payrollReportRevision || !response.preparation) { return; }
      this.groupPreparation = {handle: response.preparation, context: context,
        key: {groupId: context.target, from: context.from, to: context.to}};
    });
  }

  hasGroupHourlyComparison(key: GroupPayrollReportKey): boolean {
    this.ensureSessionOwner();
    const prepared = this.groupPreparation;
    return !!prepared && !!key && prepared.context.owner === this.sessionOwner
      && prepared.context.revision === this.payrollReportRevision
      && this.groupPayrollReportKey(prepared.key) === this.groupPayrollReportKey(key);
  }

  getGroupHourlyComparison(key: GroupPayrollReportKey): Observable<HourlyComparisonRow[]> {
    if (!this.hasGroupHourlyComparison(key)) { return Observable.throw(new Error(COMPARISON_CONTEXT_UNAVAILABLE)); }
    const prepared = this.groupPreparation;
    return this.userService.getGroupHourlyComparison(prepared.handle)
      .filter(() => this.groupPreparation === prepared && this.hasGroupHourlyComparison(key));
  }

  loadDealPayrollBreakdown(
    userId: string,
    month: string,
    refresh: boolean = false
  ): Observable<DealPayrollBreakdown> {
    return this.requiredDealPayrollService().getBreakdown(userId, month, refresh);
  }

  trackNextReportProgress(
    progressId: string,
    resultHandler: (successful: boolean) => void
  ): void {
    this.requiredUserReportService().trackNextReportProgress(progressId, resultHandler);
  }

  cancelNextReportProgress(): void {
    this.requiredUserReportService().cancelNextReportProgress();
  }

  getReportByUserId(from: string, to: string, id: string) {
    return this.requiredUserReportService().getReportByUserId(from, to, id);
  }

  getReportProgress(progressId: string) {
    return this.requiredUserReportService().getReportProgress(progressId);
  }

  getReportByUserIdOff(from: string, to: string, id: string) {
    return this.requiredUserReportService().getReportByUserIdOff(from, to, id);
  }

  getReportByGroupIdOff(groupId: string, date: string) {
    return this.requiredUserReportService().getReportByGroupIdOff(groupId, date);
  }

  setData(a) {
    this.data = [];
    this.data.push(...a);
  }

  getData() {
    return this.data;
  }

  filter(report: Report) {
    let index: number = this.reportsMin.indexOf(report);
    if (index > -1) {
      this.reportsMin.splice(index, 1);
      this.filteredReports.push(report);
    } else {
      index = this.filteredReports.indexOf(report);
      if (index > -1) {
        this.filteredReports.splice(index, 1);
        this.reportsMin.push(report);
      }
    }
  }

  clearRowNames() {
    this.rowNames = [];
  }

  rememberGroupPayrollReport(key: GroupPayrollReportKey, reports: Report[]): void {
    this.ensureSessionOwner();
    const expectedMonth = key && dealPayrollMonthFromRange(key.from, key.to);
    if (!key || !key.groupId || !expectedMonth || !reports) {
      return;
    }
    const previous = this.groupPayrollOwner === key.groupId ? this.dealPayrollBreakdowns : {};
    const retained: {[key: string]: DealPayrollBreakdown} = {};
    const retention = {expectedMonth: expectedMonth, previous: previous, retained: retained};
    reports.forEach((report: Report) => this.retainReportBreakdown(report, retention));
    this.dealPayrollBreakdowns = retained;
    this.groupPayrollOwner = key.groupId;
    this.groupPayrollReportKeyValue = this.groupPayrollReportKey(key);
    this.groupPayrollReports = this.toCompactReports(reports, expectedMonth);
  }

  getCurrentDealPayrollMonth(): string {
    this.ensureSessionOwner();
    if (!this.reportDate) {
      return this.currentDealPayrollMonth;
    }
    const match = REPORT_DATE_PATTERN.exec(this.reportDate);
    this.currentDealPayrollMonth = match ? dealPayrollMonthFromRange(
      match[REPORT_FROM_CAPTURE_INDEX], match[REPORT_TO_CAPTURE_INDEX]) : null;
    return this.currentDealPayrollMonth;
  }

  rememberDealPayrollBreakdown(breakdown: DealPayrollBreakdown): void {
    this.ensureSessionOwner();
    if (!this.hasCompleteTaskLineMemory(breakdown)) {
      return;
    }
    this.dealPayrollBreakdowns[this.dealPayrollBreakdownKey(breakdown.userId, breakdown.month)]
      = this.copyBreakdown(breakdown);
  }

  getDealPayrollBreakdown(userId: string, month: string): DealPayrollBreakdown {
    this.ensureSessionOwner();
    const value = this.dealPayrollBreakdowns[this.dealPayrollBreakdownKey(userId, month)];
    return isDealPayrollIdentityMatch(userId, month, value)
      && value.formulaVersion === DEAL_PAYROLL_FORMULA_VERSION ? value : undefined;
  }

  getGroupPayrollUserReport(key: GroupPayrollReportKey, userId: string): Report {
    this.ensureSessionOwner();
    if (!key || this.groupPayrollReportKeyValue !== this.groupPayrollReportKey(key)) {
      return undefined;
    }
    const report = this.groupPayrollReports.filter((candidate: Report) =>
      candidate && String(candidate.userID) === String(userId))[FIRST_REPORT_INDEX];
    if (!report || !isPlanfixDealPayroll(report) || !report.dealPayrollBreakdown) {
      return undefined;
    }
    const reference = report.dealPayrollBreakdown;
    const cached = this.getDealPayrollBreakdown(reference.userId, reference.month);
    if (!hasExactDealPayrollMemoryMatch(reference, cached)) {
      return undefined;
    }
    return Object.assign({}, report, {
      dealPayrollBreakdown: Object.assign({}, reference, {taskLines: cached.taskLines.slice()})
    });
  }

  toCompactReports(reports: Report[], expectedMonth: string): Report[] {
    if (expectedMonth) {
      this.currentDealPayrollMonth = expectedMonth;
    }
    return (reports || []).map((report: Report) => {
      const breakdown = report && report.dealPayrollBreakdown;
      if (!breakdown) {
        return report;
      }
      if (!isDealPayrollIdentityMatch(report.userID, expectedMonth, breakdown)
        && !isDealPayrollPeriodDiagnostic(report.userID, breakdown)) {
        const sanitized = Object.assign({}, report);
        delete sanitized.dealPayrollBreakdown;
        return sanitized;
      }
      const compact = Object.assign({}, breakdown);
      delete compact.taskLines;
      return Object.assign({}, report, {dealPayrollBreakdown: compact});
    });
  }

  clearPayrollMemory(): void {
    this.freshReportSignature = null;
    this.groupPreparation = null;
    this.groupPreparationRequest = null;
    this.dealPayrollBreakdowns = {};
    this.groupPayrollReportKeyValue = REPORT_SERVICE_EMPTY_VALUE;
    this.groupPayrollOwner = REPORT_SERVICE_EMPTY_VALUE;
    this.groupPayrollReports = [];
    this.sessionOwner = REPORT_SERVICE_EMPTY_VALUE;
    this.currentDealPayrollMonth = null;
    this.reportsMin = [];
    this.filteredReports = [];
    this.allReports = [];
  }

  invalidatePayrollReports(): void {
    this.payrollReportRevision += NEXT_REPORT_REVISION;
    this.clearPayrollMemory();
    this.reportStorage.removeItem(FINANCIAL_REPORT_STORAGE_KEY);
    this.payrollInvalidation.next();
  }

  captureFreshReportContext(from: string, to: string, target: string): FreshReportContext {
    this.ensureSessionOwner();
    return {owner: this.sessionOwner, revision: this.payrollReportRevision, from: from, to: to, target: target};
  }

  rememberFreshReport(reports: Object[], context: FreshReportContext): void {
    this.ensureSessionOwner();
    if (context.owner !== this.sessionOwner || context.revision !== this.payrollReportRevision) { return; }
    this.freshReportSignature = this.reportSignature(JSON.stringify(reports), context);
  }

  hasFreshReport(raw: string, selection: ReportSelectionContext): boolean {
    const context = this.captureFreshReportContext(selection.from, selection.to, selection.target);
    return this.freshReportSignature === this.reportSignature(raw, context);
  }

  private reportSignature(raw: string, context: FreshReportContext): string {
    return JSON.stringify([context.owner, context.revision, context.from, context.to, context.target, raw]);
  }

  private retainReportBreakdown(
    report: Report,
    context: PayrollRetentionContext
  ): void {
    const breakdown = report && report.dealPayrollBreakdown;
    if (!breakdown || !isDealPayrollIdentityMatch(report.userID, context.expectedMonth, breakdown)) {
      return;
    }
    const memoryKey = this.dealPayrollBreakdownKey(breakdown.userId, breakdown.month);
    if (this.hasCompleteTaskLineMemory(breakdown)) {
      context.retained[memoryKey] = this.copyBreakdown(breakdown);
    } else if (context.previous[memoryKey]
      && hasExactDealPayrollMemoryMatch(breakdown, context.previous[memoryKey])) {
      context.retained[memoryKey] = context.previous[memoryKey];
    }
  }

  private hasCompleteTaskLineMemory(breakdown: DealPayrollBreakdown): boolean {
    return !!breakdown
      && breakdown.formulaVersion === DEAL_PAYROLL_FORMULA_VERSION
      && isDealPayrollIdentityMatch(breakdown.userId, breakdown.month, breakdown)
      && !!breakdown.taskLines
      && typeof breakdown.taskLineCount === NUMBER_VALUE_TYPE
      && breakdown.taskLineCount >= MINIMUM_TASK_LINE_COUNT
      && breakdown.taskLineCount === breakdown.taskLines.length;
  }

  private copyBreakdown(breakdown: DealPayrollBreakdown): DealPayrollBreakdown {
    return Object.assign({}, breakdown, {taskLines: breakdown.taskLines.slice()});
  }

  private dealPayrollBreakdownKey(userId: string, month: string): string {
    return String(userId) + PAYROLL_KEY_SEPARATOR + String(month);
  }

  private groupPayrollReportKey(key: GroupPayrollReportKey): string {
    return [key.groupId, key.from, key.to].join(PAYROLL_KEY_SEPARATOR);
  }

  private ensureSessionOwner(): void {
    const user = this.userService && this.userService.getLocal ? this.userService.getLocal() : null;
    const owner = user && user.id ? String(user.id) : REPORT_SERVICE_EMPTY_VALUE;
    if (this.sessionOwner !== owner) {
      this.clearPayrollMemory();
      this.sessionOwner = owner;
    }
  }

  private requiredUserReportService(): UserReportService {
    if (!this.userReportService) {
      throw new Error(USER_REPORT_SERVICE_REQUIRED);
    }
    return this.userReportService;
  }

  private requiredDealPayrollService(): DealPayrollService {
    if (!this.dealPayrollService) {
      throw new Error(DEAL_PAYROLL_SERVICE_REQUIRED);
    }
    return this.dealPayrollService;
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/report.service.ts